import { useEffect, useState } from 'react';
import { Badge, Button, Form, Modal, Offcanvas, Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { ExamServices } from '../../../../Services/examServices';
import { InvigilatorService } from '../../../../Services/invigilatorService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faPencil, faPlus, faTrash, faUserPlus, faUsers } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';

interface IInvigilatorExamTable {
  examId: string;
  invigilatorData: any[];
  reload: () => void;
}

export default function InvigilatorExamTable({ examId, invigilatorData, reload }: IInvigilatorExamTable) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [studentMap, setStudentMap] = useState<Map<string, any[]>>(new Map());
  const [loadingStudents, setLoadingStudents] = useState<string | null>(null);

  // Edit offcanvas state
  const [editInvigilator, setEditInvigilator] = useState<any>(null);
  const [allExamStudents, setAllExamStudents] = useState<any[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [unassignedInvigilators, setUnassignedInvigilators] = useState<any[]>([]);
  const [selectedInvigilators, setSelectedInvigilators] = useState<any[]>([]);
  const [loadingUnassigned, setLoadingUnassigned] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createCount, setCreateCount] = useState(1);
  const [creating, setCreating] = useState(false);

  const [removeRow, setRemoveRow] = useState<any>(null);
  const [removing, setRemoving] = useState(false);

  async function fetchStudentsForInvigilator(invigilatorRow: any) {
    const invigilatorId = invigilatorRow?.invigilator?._id;
    if (!invigilatorId) return;

    const allottedExam = invigilatorRow.invigilator.allottedExams?.find(
      (ae: any) => String(ae.examId) === String(examId)
    );
    const studentIds: string[] = allottedExam?.students || [];

    if (studentIds.length === 0) {
      setStudentMap(prev => new Map(prev).set(invigilatorId, []));
      return;
    }

    setLoadingStudents(invigilatorId);
    try {
      const res = await ExamServices.getAllMappedUserLean(examId);
      if (res.status === 200) {
        const allUsers = res.data.data?.users || [];
        const assigned = allUsers.filter((u: any) =>
          studentIds.includes(String(u.student?._id))
        );
        setStudentMap(prev => new Map(prev).set(invigilatorId, assigned));
      }
    } catch {
      toast.error('Failed to load students');
    } finally {
      setLoadingStudents(null);
    }
  }

  function handleToggleExpand(row: any) {
    const id = row?.invigilator?._id;
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (!studentMap.has(id)) {
      fetchStudentsForInvigilator(row);
    }
  }

  async function handleEditClick(row: any) {
    setEditInvigilator(row);
    setLoadingOptions(true);

    const allottedExam = row.invigilator.allottedExams?.find(
      (ae: any) => String(ae.examId) === String(examId)
    );
    const currentStudentIds: string[] = (allottedExam?.students || []).map(String);

    try {
      const res = await ExamServices.getAllMappedUserLean(examId);
      if (res.status === 200) {
        const options = (res.data.data?.users || []).map((u: any) => ({
          value: u.student?._id,
          label: `${u.student?.name || ''} ${u.student?.last_name || ''} (${u.student?.username || u.student?.registrationNumber || ''})`.trim(),
        }));
        setAllExamStudents(options);
        setSelectedStudents(options.filter((o: any) => currentStudentIds.includes(String(o.value))));
      }
    } catch {
      toast.error('Failed to load students');
    } finally {
      setLoadingOptions(false);
    }
  }

  async function handleSave() {
    if (!editInvigilator) return;
    setSaving(true);
    try {
      const payload = {
        examId,
        students: selectedStudents.map((s: any) => s.value),
        _id: editInvigilator.invigilator.allottedExams?.find(
          (ae: any) => String(ae.examId) === String(examId)
        )?._id,
      };
      const res = await InvigilatorService.updateAllotedExam(editInvigilator.invigilator._id, payload);
      if (res.status === 200) {
        if (res.data?.alreadyAssigned?.length > 0) {
          toast.warning(
            <>
              <span className="fw-bold">Some students already assigned to other invigilator: </span>
              {res.data.alreadyAssigned.map((s: any) => (
                <Badge className="mx-1" key={s.id} pill>{s.name}</Badge>
              ))}
            </>
          );
        } else {
          toast.success('Students updated');
        }
        setStudentMap(prev => {
          const next = new Map(prev);
          next.delete(editInvigilator.invigilator._id);
          return next;
        });
        reload();
        handleCloseEdit();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.response?.data || 'Something went wrong');
    } finally {
      setSaving(false);
    }
  }

  function handleCloseEdit() {
    setEditInvigilator(null);
    setAllExamStudents([]);
    setSelectedStudents([]);
  }

  // --- Assign existing invigilators ---
  async function openAssignModal() {
    setShowAssignModal(true);
    setLoadingUnassigned(true);
    setSelectedInvigilators([]);
    try {
      const res = await InvigilatorService.getUnassignedForExam(examId);
      if (res.status === 200) {
        setUnassignedInvigilators(
          (res.data.invigilators || []).map((inv: any) => ({
            value: inv._id,
            label: `${inv.name} (${inv.email})`,
          }))
        );
      }
    } catch {
      toast.error('Failed to load invigilators');
    } finally {
      setLoadingUnassigned(false);
    }
  }

  async function handleAssign() {
    if (selectedInvigilators.length === 0) {
      toast.error('Select at least one invigilator');
      return;
    }
    setAssigning(true);
    try {
      const res = await InvigilatorService.assignToExam(
        examId,
        selectedInvigilators.map((i: any) => i.value)
      );
      if (res.status === 200) {
        toast.success(res.data.message || 'Invigilators assigned');
        reload();
        setShowAssignModal(false);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Something went wrong');
    } finally {
      setAssigning(false);
    }
  }

  // --- Remove invigilator and reassign ---
  async function handleRemove() {
    if (!removeRow) return;
    setRemoving(true);
    try {
      const res = await InvigilatorService.removeFromExam(
        examId,
        removeRow.invigilator._id,
      );
      if (res.status === 200) {
        toast.success(res.data.message || 'Invigilator removed');
        reload();
        setRemoveRow(null);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Something went wrong');
    } finally {
      setRemoving(false);
    }
  }

  // --- Create new invigilators ---
  async function handleCreate() {
    if (createCount < 1 || createCount > 50) {
      toast.error('Count must be between 1 and 50');
      return;
    }
    setCreating(true);
    try {
      const res = await InvigilatorService.createForExam(examId, createCount);
      if (res.status === 200) {
        toast.success(res.data.message || 'Invigilators created');
        reload();
        setShowCreateModal(false);
        setCreateCount(1);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Something went wrong');
    } finally {
      setCreating(false);
    }
  }

  return (
    <>
      {/* Action buttons */}
      <div className="d-flex gap-2 mb-3">
        <Button variant="primary" size="sm" onClick={openAssignModal}>
          <FontAwesomeIcon icon={faUserPlus} className="me-1" />
          Assign Existing Invigilators
        </Button>
        <Button variant="outline-primary" size="sm" onClick={() => setShowCreateModal(true)}>
          <FontAwesomeIcon icon={faPlus} className="me-1" />
          Create New Invigilators
        </Button>
      </div>

      {invigilatorData.length === 0 ? (
        <div className="text-muted p-4 text-center">
          No invigilators assigned to this exam yet.
        </div>
      ) : (
        <Table striped borderless responsive size="sm">
          <thead>
            <tr>
              <th style={{ width: 30 }}></th>
              <th>#</th>
              <th>Name</th>
              <th>Email</th>
              <th>Status</th>
              <th className="text-center">Total Students</th>
              <th className="text-center">Completed</th>
              <th className="text-center">Student Sub.</th>
              <th className="text-center">Invigilator Sub.</th>
              <th className="text-center">Remaining</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {invigilatorData.map((row: any, idx: number) => {
              const id = row?.invigilator?._id;
              const isExpanded = expandedId === id;
              const students = studentMap.get(id);
              const isLoadingThis = loadingStudents === id;

              return (
                <>
                  <tr key={id || idx}>
                    <td
                      className="text-center"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleToggleExpand(row)}
                    >
                      <FontAwesomeIcon
                        icon={isExpanded ? faChevronDown : faChevronRight}
                        size="xs"
                        className="text-secondary"
                      />
                    </td>
                    <td className="text-secondary">{idx + 1}</td>
                    <td>{row?.invigilator?.name || '—'}</td>
                    <td className="text-muted" style={{ fontSize: '12px' }}>
                      {row?.invigilator?.email || '—'}
                    </td>
                    <td>
                      <Badge bg={row?.invigilator?.status === 'ACTIVE' ? 'success' : 'secondary'}>
                        {row?.invigilator?.status || '—'}
                      </Badge>
                    </td>
                    <td className="text-center">{row?.totalExamMappings ?? 0}</td>
                    <td className="text-center">{row?.examMappingsWithCompletionStatus ?? 0}</td>
                    <td className="text-center">{row?.examMappingsWithStudentSubmitted ?? 0}</td>
                    <td className="text-center">{row?.examMappingsWithInvigilatorSubmitted ?? 0}</td>
                    <td className="text-center">
                      {(row?.totalExamMappings ?? 0) - (row?.examMappingsWithCompletionStatus ?? 0)}
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEditClick(row)}
                        >
                          <FontAwesomeIcon icon={faPencil} size="xs" className="me-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => setRemoveRow(row)}
                        >
                          <FontAwesomeIcon icon={faTrash} size="xs" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr key={`${id}-students`}>
                      <td colSpan={11} className="p-0">
                        <div className="bg-light px-4 py-2" style={{ fontSize: '13px' }}>
                          <div className="fw-semibold mb-1">
                            <FontAwesomeIcon icon={faUsers} className="me-1" />
                            Assigned Students ({students?.length ?? '...'})
                          </div>
                          {isLoadingThis ? (
                            <div className="text-muted py-2">
                              <Spinner animation="border" size="sm" className="me-2" />
                              Loading...
                            </div>
                          ) : !students || students.length === 0 ? (
                            <div className="text-muted py-1">No students assigned</div>
                          ) : (
                            <Table size="sm" borderless className="mb-0 bg-transparent">
                              <thead>
                                <tr className="text-muted" style={{ fontSize: '11px' }}>
                                  <th style={{ width: 40 }}>#</th>
                                  <th>Name</th>
                                  <th>Reg. No</th>
                                  <th>Email</th>
                                </tr>
                              </thead>
                              <tbody>
                                {students.map((s: any, sIdx: number) => (
                                  <tr key={s.student?._id || sIdx}>
                                    <td className="text-secondary">{sIdx + 1}</td>
                                    <td>{s.student?.name} {s.student?.last_name}</td>
                                    <td className="text-muted">{s.student?.registrationNumber || s.student?.username || '—'}</td>
                                    <td className="text-muted">{s.student?.email || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </Table>
      )}

      {/* Edit Students Offcanvas */}
      <Offcanvas
        show={!!editInvigilator}
        onHide={handleCloseEdit}
        placement="end"
        style={{ width: '450px' }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            Manage Students
            <div className="text-muted" style={{ fontSize: '12px' }}>
              {editInvigilator?.invigilator?.name} ({editInvigilator?.invigilator?.email})
            </div>
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="d-flex flex-column justify-content-between h-100">
            <div>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Assigned Students</Form.Label>
                {loadingOptions ? (
                  <div className="text-muted py-3 text-center">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading students...
                  </div>
                ) : (
                  <>
                    <Select
                      options={allExamStudents}
                      value={selectedStudents}
                      onChange={(e: any) => setSelectedStudents(e || [])}
                      isMulti
                      placeholder="Select students..."
                      closeMenuOnSelect={false}
                    />
                    {allExamStudents.length > 0 && (
                      <div className="d-flex gap-2 mt-1 justify-content-end">
                        <Badge
                          pill
                          bg="secondary"
                          style={{ cursor: 'pointer' }}
                          onClick={() => setSelectedStudents(allExamStudents)}
                        >
                          Select All
                        </Badge>
                        <Badge
                          pill
                          bg="outline-secondary"
                          className="border"
                          style={{ cursor: 'pointer', color: '#666' }}
                          onClick={() => setSelectedStudents([])}
                        >
                          Clear All
                        </Badge>
                      </div>
                    )}
                  </>
                )}
              </Form.Group>

              {selectedStudents.length > 0 && (
                <div className="text-muted" style={{ fontSize: '12px' }}>
                  {selectedStudents.length} student{selectedStudents.length !== 1 ? 's' : ''} selected
                </div>
              )}
            </div>

            <Button
              variant="primary"
              className="w-100"
              onClick={handleSave}
              disabled={saving || loadingOptions}
            >
              {saving ? <><Spinner size="sm" animation="border" className="me-1" /> Saving...</> : 'Save Changes'}
            </Button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Assign Existing Invigilators Modal */}
      <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Assign Invigilators to Exam</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingUnassigned ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading available invigilators...
            </div>
          ) : unassignedInvigilators.length === 0 ? (
            <div className="text-muted text-center py-3">
              No unassigned invigilators available. Create new ones instead.
            </div>
          ) : (
            <Form.Group>
              <Form.Label className="fw-semibold">Select Invigilators</Form.Label>
              <Select
                options={unassignedInvigilators}
                value={selectedInvigilators}
                onChange={(e: any) => setSelectedInvigilators(e || [])}
                isMulti
                placeholder="Search and select invigilators..."
                closeMenuOnSelect={false}
              />
              {unassignedInvigilators.length > 0 && (
                <div className="d-flex gap-2 mt-1 justify-content-end">
                  <Badge pill bg="secondary" style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedInvigilators(unassignedInvigilators)}
                  >
                    Select All
                  </Badge>
                  <Badge pill bg="outline-secondary" className="border"
                    style={{ cursor: 'pointer', color: '#666' }}
                    onClick={() => setSelectedInvigilators([])}
                  >
                    Clear
                  </Badge>
                </div>
              )}
              {selectedInvigilators.length > 0 && (
                <div className="text-muted mt-2" style={{ fontSize: '12px' }}>
                  {selectedInvigilators.length} invigilator{selectedInvigilators.length !== 1 ? 's' : ''} selected
                </div>
              )}
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAssignModal(false)}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleAssign}
            disabled={assigning || selectedInvigilators.length === 0}
          >
            {assigning ? <><Spinner size="sm" animation="border" className="me-1" /> Assigning...</> : 'Assign'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create New Invigilators Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create New Invigilators</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted" style={{ fontSize: '13px' }}>
            New invigilators will be auto-created with generated credentials and assigned to this exam with no students.
          </p>
          <Form.Group>
            <Form.Label className="fw-semibold">Number of Invigilators</Form.Label>
            <Form.Control
              type="number"
              min={1}
              max={50}
              value={createCount}
              onChange={(e) => setCreateCount(Number(e.target.value))}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={creating || createCount < 1}
          >
            {creating ? <><Spinner size="sm" animation="border" className="me-1" /> Creating...</> : `Create ${createCount}`}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Remove Invigilator + Reassign Modal */}
      <Modal show={!!removeRow} onHide={() => setRemoveRow(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">Remove Invigilator</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {removeRow && (
            <>
              <p style={{ fontSize: '13px' }}>
                Remove <b>{removeRow.invigilator?.name}</b> ({removeRow.invigilator?.email}) from this exam?
              </p>
              {(removeRow.totalExamMappings ?? 0) > 0 ? (
                <div className="alert alert-warning py-2" style={{ fontSize: '13px' }}>
                  This invigilator has <b>{removeRow.totalExamMappings}</b> student(s) assigned.
                  They will be automatically redistributed to other invigilators based on the exam's
                  student-per-invigilator limit. If no invigilator has capacity, new ones will be created.
                </div>
              ) : (
                <div className="alert alert-info py-2" style={{ fontSize: '13px' }}>
                  No students assigned. The invigilator will simply be unlinked from this exam.
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setRemoveRow(null)}>Cancel</Button>
          <Button variant="danger" onClick={handleRemove} disabled={removing}>
            {removing ? <><Spinner size="sm" animation="border" className="me-1" /> Removing...</> : 'Remove'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
